import type { JSX, MouseEvent } from 'react';
import { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider';

export function WorkspaceSwitcher(): JSX.Element | null {
  const auth = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  if (!auth.bootstrap) {
    return null;
  }

  const hasCompanyWorkspace = auth.bootstrap.memberships.length > 0;
  const hasPlatformWorkspace = Boolean(auth.canAccessPlatform);
  if (!hasCompanyWorkspace && !hasPlatformWorkspace) {
    return null;
  }

  const currentLabel =
    auth.bootstrap.mode === 'company'
      ? 'Company Workspace'
      : hasPlatformWorkspace
        ? 'Platform Workspace'
        : 'Account';

  const openMenu = (event: MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = (): void => {
    setAnchorEl(null);
  };

  const switchToPlatform = async (): Promise<void> => {
    if (!auth.selectIdentity) {
      return;
    }

    closeMenu();
    setIsSwitching(true);
    setSwitchError(null);

    try {
      await auth.selectIdentity();
      navigate('/platform');
    } catch (error) {
      setSwitchError(error instanceof Error ? error.message : 'Unable to switch workspace');
    } finally {
      setIsSwitching(false);
    }
  };

  const switchToCompany = (): void => {
    closeMenu();
    setSwitchError(null);
    navigate('/select-company');
  };

  return (
    <Stack spacing={0.5} alignItems="flex-end">
      <Button
        variant="outlined"
        size="small"
        onClick={openMenu}
        disabled={isSwitching}
      >
        {isSwitching ? 'Switching...' : currentLabel}
      </Button>
      {switchError && (
        <Typography variant="caption" color="error">
          {switchError}
        </Typography>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
      >
        <MenuItem disabled>{currentLabel}</MenuItem>
        {auth.bootstrap.mode === 'company' && hasPlatformWorkspace && (
          <MenuItem onClick={() => { void switchToPlatform(); }}>
            Platform Workspace
          </MenuItem>
        )}
        {auth.bootstrap.mode === 'identity' && hasCompanyWorkspace && (
          <MenuItem onClick={switchToCompany}>
            Company Workspace
          </MenuItem>
        )}
      </Menu>
    </Stack>
  );
}
